import type { AnswerInputProps } from '../QuestionRenderer'

export function NumberSliderInput({
  question,
  value,
  onChange,
  onSubmit,
}: AnswerInputProps<number>) {
  const min = Number(question.settings.sliderMin ?? 0)
  const max = Number(question.settings.sliderMax ?? 100)
  const step = Number(question.settings.sliderStep ?? 1)
  const current = value ?? min
  const percent = max === min ? 0 : ((current - min) / (max - min)) * 100
  const tickLabels = question.settings.sliderTickLabels ?? []
  const currentLabel = formatSliderValue(current, question)

  return (
    <div className="flex flex-col gap-4">
      {question.settings.sliderShowValue !== false && (
        <div
          className="inline-flex w-fit min-w-16 items-center justify-center rounded-full px-4 py-2 font-semibold"
          style={{
            background: 'var(--form-accent,#6366F1)',
            color: 'var(--form-button-text,#FFFFFF)',
            fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
          }}
        >
          {currentLabel}
        </div>
      )}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={current}
        onChange={(event) => onChange(Number(event.target.value))}
        onKeyDown={(event) => {
          if (event.key === 'Enter') onSubmit()
        }}
        className="h-3 w-full cursor-pointer appearance-none rounded-full outline-none"
        style={{
          background: `linear-gradient(90deg, var(--form-accent,#6366F1) ${percent}%, color-mix(in srgb, var(--form-accent,#6366F1) 18%, transparent) ${percent}%)`,
        }}
      />
      {question.settings.sliderShowTicks !== false && tickLabels.length > 0 ? (
        <div
          className="grid gap-2 text-center text-xs"
          style={{
            gridTemplateColumns: `repeat(${tickLabels.length}, minmax(0, 1fr))`,
            color: 'var(--form-answer,#64748B)',
          }}
        >
          {tickLabels.map((label) => (
            <span key={label} className="min-w-0 truncate">
              {label}
            </span>
          ))}
        </div>
      ) : null}
      <div
        className="flex justify-between gap-4 text-sm"
        style={{
          color: 'var(--form-answer,#64748B)',
          fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
          fontSize: 'var(--form-meta-size,0.9375rem)',
        }}
      >
        <span>{question.settings.startLabel ?? min}</span>
        <span>{question.settings.endLabel ?? max}</span>
      </div>
    </div>
  )
}

function formatSliderValue(value: number, question: AnswerInputProps<number>['question']) {
  const decimals =
    question.settings.numberDecimals === undefined
      ? countDecimals(Number(question.settings.sliderStep ?? 1))
      : Number(question.settings.numberDecimals)
  const formatted = question.settings.numberUseThousands
    ? new Intl.NumberFormat('zh-TW', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(value)
    : value.toFixed(decimals)

  return [
    question.settings.numberPrefix,
    formatted,
    question.settings.numberUnit,
    question.settings.numberSuffix,
  ]
    .filter(Boolean)
    .join(' ')
}

function countDecimals(value: number) {
  if (!Number.isFinite(value)) return 0
  const [, decimals = ''] = String(value).split('.')
  return Math.min(decimals.length, 6)
}
